import type { DrawMode, InteractionMode } from "../editorTypes";

interface MapToolbarProps {
  interactionMode: InteractionMode;
  drawMode: DrawMode;
  onSetInteractionMode: (mode: InteractionMode) => void;
  onSetDrawMode: (mode: DrawMode) => void;
  canUndo: boolean;
  canRedo: boolean;
  onUndo: () => void;
  onRedo: () => void;
  hasSelection: boolean;
  onDelete: () => void;
  onExportGeoJson: () => void;
  onSaveDraft: () => void;
  onSendReview: () => void;
  saving: boolean;
}

function ToolButton({
  active,
  icon,
  label,
  onClick,
  disabled,
}: {
  active?: boolean;
  icon: string;
  label: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={label}
      className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors whitespace-nowrap ${
        disabled
          ? "text-foreground-300 cursor-not-allowed"
          : active
            ? "bg-primary-500 text-white cursor-pointer"
            : "text-foreground-600 hover:bg-background-100 cursor-pointer"
      }`}
    >
      <i className={`${icon} text-sm`} />
      <span className="hidden xl:inline">{label}</span>
    </button>
  );
}

function Separator() {
  return <div className="w-px h-6 bg-background-200 mx-1" />;
}

export default function MapToolbar({
  interactionMode,
  drawMode,
  onSetInteractionMode,
  onSetDrawMode,
  canUndo,
  canRedo,
  onUndo,
  onRedo,
  hasSelection,
  onDelete,
  onExportGeoJson,
  onSaveDraft,
  onSendReview,
  saving,
}: MapToolbarProps) {
  return (
    <div className="flex items-center gap-0.5 flex-wrap px-2 py-1.5 border-b border-background-200/70 bg-white overflow-x-auto">
      <ToolButton
        icon="ri-cursor-line"
        label="Selecionar"
        active={interactionMode === "select"}
        onClick={() => onSetInteractionMode("select")}
      />
      <ToolButton
        icon="ri-drag-move-2-line"
        label="Mover"
        active={interactionMode === "translate"}
        onClick={() => onSetInteractionMode("translate")}
      />
      <ToolButton
        icon="ri-shape-2-line"
        label="Editar vértice"
        active={interactionMode === "modify"}
        onClick={() => onSetInteractionMode("modify")}
      />

      <Separator />

      <ToolButton icon="ri-map-pin-add-line" label="Ponto" active={drawMode === "Point"} onClick={() => onSetDrawMode("Point")} />
      <ToolButton
        icon="ri-route-line"
        label="Linha"
        active={drawMode === "LineString"}
        onClick={() => onSetDrawMode("LineString")}
      />
      <ToolButton
        icon="ri-pentagon-line"
        label="Polígono"
        active={drawMode === "Polygon"}
        onClick={() => onSetDrawMode("Polygon")}
      />
      <ToolButton
        icon="ri-checkbox-blank-line"
        label="Retângulo"
        active={drawMode === "Rectangle"}
        onClick={() => onSetDrawMode("Rectangle")}
      />
      <ToolButton
        icon="ri-circle-line"
        label="Círculo aprox."
        active={drawMode === "Circle"}
        onClick={() => onSetDrawMode("Circle")}
      />

      <Separator />

      <ToolButton icon="ri-delete-bin-line" label="Excluir" onClick={onDelete} disabled={!hasSelection} />
      <ToolButton icon="ri-arrow-go-back-line" label="Desfazer" onClick={onUndo} disabled={!canUndo} />
      <ToolButton icon="ri-arrow-go-forward-line" label="Refazer" onClick={onRedo} disabled={!canRedo} />

      <Separator />

      <ToolButton
        icon="ri-ruler-line"
        label="Medir distância"
        active={interactionMode === "measure-distance"}
        onClick={() => onSetInteractionMode("measure-distance")}
      />
      <ToolButton
        icon="ri-shape-line"
        label="Medir área"
        active={interactionMode === "measure-area"}
        onClick={() => onSetInteractionMode("measure-area")}
      />

      <Separator />

      <ToolButton icon="ri-download-2-line" label="Exportar GeoJSON" onClick={onExportGeoJson} />
      <ToolButton
        icon="ri-save-line"
        label={saving ? "Salvando..." : "Salvar rascunho"}
        onClick={onSaveDraft}
        disabled={!hasSelection || saving}
      />
      <ToolButton
        icon="ri-send-plane-line"
        label="Enviar para revisão"
        onClick={onSendReview}
        disabled={!hasSelection || saving}
      />
    </div>
  );
}
